// geographycontent6.js
// Section Six — Population and Urbanisation
// Grade9Ready Geography content module
//
// Covers:
// 74 Population Distribution
// 76 Population Density
// 77 Population Growth
// 79 Population Structure
// 80 Migration
// 84 Urbanisation
// 85 Urban Issues
// 87 Squatter Settlements
// 88 Urbanisation Environmental Issues
// 91 Revision Summary for Section Six

export const geographyContent6 = {
  id: "geography-section-6",
  subject: "Geography",
  sectionNumber: 6,
  title: "Population and Urbanisation",
  slug: "population-and-urbanisation",
  examBoardLink:
    "KS3 Geography: human geography, population, urbanisation, development and environmental change",
  intro:
    "This section explains how and why people are distributed unevenly across the world, why populations grow or decline, how population structure changes, why people migrate, and how urbanisation creates opportunities and challenges. It also explores squatter settlements and environmental issues linked to rapidly growing cities.",
  keySkills: [
    "Describe patterns of population distribution using accurate geographical language.",
    "Calculate and interpret population density.",
    "Explain the causes and effects of population growth.",
    "Interpret population pyramids and link them to development.",
    "Explain push and pull factors affecting migration.",
    "Evaluate the opportunities and challenges created by urbanisation.",
    "Use case-study-style detail to support extended answers."
  ],

  topics: [
    {
      id: "geo6-1-population-distribution",
      title: "Population Distribution",
      slug: "population-distribution",
      pageRef: "Source booklet p.74",
      learningObjectives: [
        "Define population distribution.",
        "Describe global patterns of where people live.",
        "Explain why some places are densely or sparsely populated."
      ],
      keywords: [
        {
          term: "Population distribution",
          definition:
            "The pattern of where people live across an area."
        },
        {
          term: "Densely populated",
          definition:
            "An area where many people live close together."
        },
        {
          term: "Sparsely populated",
          definition:
            "An area where few people live."
        },
        {
          term: "Physical factors",
          definition:
            "Natural features that affect where people live, such as climate, relief, water supply and soil."
        },
        {
          term: "Human factors",
          definition:
            "People-made or social factors that affect where people live, such as jobs, transport, services and history."
        },
        {
          term: "Relief",
          definition:
            "The height and shape of the land."
        },
        {
          term: "Climate",
          definition:
            "The long-term pattern of weather in a place."
        }
      ],
      tutorial: [
        {
          heading: "What population distribution means",
          body:
            "Population distribution is about pattern. It asks where people live and where they do not. People are not spread evenly across the world. Some places, such as river valleys, lowland areas and coastal regions, have many people. Other places, such as deserts, polar regions, mountains and dense rainforest, have fewer people."
        },
        {
          heading: "Why some places attract people",
          body:
            "People often live in areas with flat land, fertile soil, reliable water, a mild climate, good transport links and employment. These areas make farming, building, trade and everyday life easier."
        },
        {
          heading: "Why some places have few people",
          body:
            "Places may be sparsely populated if they are too hot, too cold, too dry, too mountainous, too remote, or have poor soils. These physical limits make settlement, farming and transport more difficult."
        }
      ],
      workedExamples: [
        {
          question: "Explain why river valleys are often densely populated.",
          answer:
            "River valleys often have reliable water supplies, fertile alluvial soils and relatively flat land. This supports farming, settlement, transport and trade, so more people are able to live there."
        },
        {
          question: "Explain why deserts are usually sparsely populated.",
          answer:
            "Deserts have very low rainfall, limited water supplies and extreme temperatures. This makes farming difficult and creates challenges for settlements, so fewer people live there."
        }
      ],
      commonMisconceptions: [
        "Population distribution is not the same as population density. Distribution is the pattern; density is the number of people per unit area.",
        "Sparse population does not always mean no people. It means few people compared with the size of the area.",
        "Human and physical factors often work together. For example, a port city may grow because of both coastal location and trade."
      ],
      quickCheck: [
        {
          question: "What does population distribution describe?",
          options: [
            "The pattern of where people live",
            "The number of babies born each year",
            "The number of people per square kilometre",
            "The percentage of people over 65"
          ],
          answer: "The pattern of where people live",
          explanation:
            "Population distribution describes the spatial pattern of settlement."
        },
        {
          question: "Which area is most likely to be sparsely populated?",
          options: [
            "A fertile river valley",
            "A major coastal city",
            "A hot desert",
            "A transport hub"
          ],
          answer: "A hot desert",
          explanation:
            "Hot deserts often have limited water and extreme temperatures."
        }
      ],
      examPractice: [
        {
          question:
            "Explain two physical factors that influence population distribution.",
          marks: 4,
          indicativeContent:
            "Climate influences distribution because very hot, cold or dry places are harder to live in, reducing population. Relief also matters because steep mountains make farming, transport and building difficult, so fewer people settle there."
        }
      ],
      grade9Move:
        "Use pattern language before explanation: clustered, linear, dispersed, dense, sparse, coastal, lowland, inland, upland. Then explain the physical or human reasons."
    },

    {
      id: "geo6-2-population-density",
      title: "Population Density",
      slug: "population-density",
      pageRef: "Source booklet p.76",
      learningObjectives: [
        "Define population density.",
        "Calculate population density.",
        "Interpret what high and low population density suggest about a place."
      ],
      keywords: [
        {
          term: "Population density",
          definition:
            "The average number of people living in a unit area, usually people per square kilometre."
        },
        {
          term: "Area",
          definition:
            "The size of a place, usually measured in square kilometres for population density."
        },
        {
          term: "High population density",
          definition:
            "Many people living in a relatively small area."
        },
        {
          term: "Low population density",
          definition:
            "Few people living in a relatively large area."
        },
        {
          term: "Overcrowding",
          definition:
            "When too many people live in an area or building for the available space and services."
        }
      ],
      tutorial: [
        {
          heading: "The formula",
          body:
            "Population density is calculated by dividing the total population by the area. The usual formula is: population density = population ÷ area. The answer is normally written as people per square kilometre."
        },
        {
          heading: "What density tells us",
          body:
            "A high density means many people live in each square kilometre on average. This can suggest urban areas, good jobs, services and transport, but it can also lead to overcrowding. A low density suggests fewer people per square kilometre, often in rural, remote or environmentally difficult areas."
        },
        {
          heading: "Density is an average",
          body:
            "Population density can hide variation. A country may have a low average density but still contain very crowded cities. Always think about scale."
        }
      ],
      workedExamples: [
        {
          question:
            "A country has 20,000,000 people and an area of 500,000 km². Calculate its population density.",
          answer:
            "20,000,000 ÷ 500,000 = 40 people per km²."
        },
        {
          question:
            "A city has a population of 3,000,000 and an area of 600 km². Calculate the population density.",
          answer:
            "3,000,000 ÷ 600 = 5,000 people per km²."
        }
      ],
      commonMisconceptions: [
        "Population density is not the total population.",
        "A large country can have a low density if its population is spread across a huge area.",
        "A high-density place is not automatically poor; many wealthy cities are densely populated."
      ],
      quickCheck: [
        {
          question:
            "What is the correct formula for population density?",
          options: [
            "Population ÷ area",
            "Area ÷ population",
            "Birth rate − death rate",
            "Population × area"
          ],
          answer: "Population ÷ area",
          explanation:
            "Population density is the total population divided by the area."
        },
        {
          question:
            "A region has 1,000,000 people and an area of 10,000 km². What is its population density?",
          options: [
            "10 people per km²",
            "100 people per km²",
            "1,000 people per km²",
            "10,000 people per km²"
          ],
          answer: "100 people per km²",
          explanation:
            "1,000,000 ÷ 10,000 = 100 people per km²."
        }
      ],
      examPractice: [
        {
          question:
            "Explain why population density can be useful but also misleading.",
          marks: 6,
          indicativeContent:
            "Population density is useful because it allows places of different sizes to be compared. It can show whether an area is crowded or sparsely populated. However, it is an average and can hide variation. A country may have empty deserts or mountains but very crowded cities, so density alone does not show the full pattern."
        }
      ],
      grade9Move:
        "Always include units in density answers: people per km². In explanation, mention that density is an average and scale matters."
    },

    {
      id: "geo6-3-population-growth",
      title: "Population Growth",
      slug: "population-growth",
      pageRef: "Source booklet p.77",
      learningObjectives: [
        "Explain why populations grow or decline.",
        "Define birth rate, death rate and natural increase.",
        "Describe reasons for rapid population growth."
      ],
      keywords: [
        {
          term: "Birth rate",
          definition:
            "The number of live births per 1,000 people per year."
        },
        {
          term: "Death rate",
          definition:
            "The number of deaths per 1,000 people per year."
        },
        {
          term: "Natural increase",
          definition:
            "Population growth that happens when the birth rate is higher than the death rate."
        },
        {
          term: "Natural decrease",
          definition:
            "Population decline that happens when the death rate is higher than the birth rate."
        },
        {
          term: "Life expectancy",
          definition:
            "The average number of years a person is expected to live."
        },
        {
          term: "Infant mortality",
          definition:
            "The number of children who die before their first birthday, usually per 1,000 live births."
        },
        {
          term: "Fertility rate",
          definition:
            "The average number of children a woman is expected to have."
        }
      ],
      tutorial: [
        {
          heading: "How population changes",
          body:
            "A population grows when more people are added than lost. This can happen through natural increase, where births are higher than deaths, or through immigration. A population can decline if deaths are higher than births or if many people emigrate."
        },
        {
          heading: "Why death rates fall",
          body:
            "Death rates often fall when healthcare, sanitation, clean water, food supply and vaccination improve. Fewer people die from disease, hunger or unsafe living conditions, so life expectancy rises."
        },
        {
          heading: "Why birth rates may stay high",
          body:
            "Birth rates may remain high where children are needed to work, support parents in old age, or where access to contraception and education is limited. Cultural expectations can also influence family size."
        }
      ],
      workedExamples: [
        {
          question:
            "A country has a birth rate of 32 per 1,000 and a death rate of 12 per 1,000. What is the natural increase?",
          answer:
            "32 − 12 = 20 per 1,000. The population is increasing naturally."
        },
        {
          question:
            "Explain how improved healthcare can cause population growth.",
          answer:
            "Improved healthcare reduces deaths from disease and childbirth complications. If the death rate falls while the birth rate remains high, natural increase becomes larger and the population grows."
        }
      ],
      commonMisconceptions: [
        "Population growth is not caused only by births; migration can also increase population.",
        "A falling birth rate does not always mean population is declining. If deaths are still lower than births, the population may continue to grow.",
        "Natural increase does not include migration."
      ],
      quickCheck: [
        {
          question:
            "What is natural increase?",
          options: [
            "Birth rate higher than death rate",
            "Death rate higher than birth rate",
            "More people leaving than arriving",
            "A rise in population density only"
          ],
          answer: "Birth rate higher than death rate",
          explanation:
            "Natural increase happens when births exceed deaths."
        },
        {
          question:
            "Which improvement is likely to reduce death rates?",
          options: [
            "Less clean water",
            "Better healthcare",
            "Lower vaccination",
            "Poorer sanitation"
          ],
          answer: "Better healthcare",
          explanation:
            "Better healthcare reduces preventable deaths."
        }
      ],
      examPractice: [
        {
          question:
            "Explain why some countries experience rapid population growth.",
          marks: 6,
          indicativeContent:
            "Rapid population growth can happen when death rates fall quickly due to better healthcare, sanitation, clean water and food supplies. If birth rates remain high because children are needed for work, old-age support or cultural reasons, natural increase is high. Immigration can also add to population growth."
        }
      ],
      grade9Move:
        "Use the phrase ‘death rates fall faster than birth rates’ when explaining rapid growth. This shows you understand the demographic transition pattern."
    },

    {
      id: "geo6-4-population-structure",
      title: "Population Structure",
      slug: "population-structure",
      pageRef: "Source booklet p.79",
      learningObjectives: [
        "Define population structure.",
        "Interpret population pyramids.",
        "Link population structure to development and future planning."
      ],
      keywords: [
        {
          term: "Population structure",
          definition:
            "The composition of a population, usually by age and sex."
        },
        {
          term: "Population pyramid",
          definition:
            "A graph showing the age and sex structure of a population."
        },
        {
          term: "Dependency ratio",
          definition:
            "The relationship between working-age people and dependants, such as children and older people."
        },
        {
          term: "Youthful population",
          definition:
            "A population with a high proportion of children and young people."
        },
        {
          term: "Ageing population",
          definition:
            "A population with a rising proportion of older people."
        },
        {
          term: "Economically active",
          definition:
            "People of working age who are able to work or are working."
        }
      ],
      tutorial: [
        {
          heading: "Reading a population pyramid",
          body:
            "Population pyramids usually show males on one side and females on the other. Age groups are stacked from youngest at the bottom to oldest at the top. A wide base means many young people. A narrow top means fewer older people."
        },
        {
          heading: "Youthful populations",
          body:
            "A youthful population often has a wide base because birth rates are high. This can create pressure for schools, healthcare, housing and future jobs."
        },
        {
          heading: "Ageing populations",
          body:
            "An ageing population has a larger proportion of older people. This can increase pressure on pensions, healthcare and social care, but older people may also contribute through work, volunteering and family support."
        }
      ],
      workedExamples: [
        {
          question:
            "What does a wide base on a population pyramid suggest?",
          answer:
            "It suggests a high birth rate and a large proportion of young people."
        },
        {
          question:
            "Explain one challenge of an ageing population.",
          answer:
            "An ageing population can increase demand for healthcare and pensions because more people are living into older age and may need support."
        }
      ],
      commonMisconceptions: [
        "A population pyramid is not always pyramid-shaped. More developed countries may have a narrower base and wider middle or top.",
        "A youthful population is not automatically a problem, but it does require investment in schools, jobs and services.",
        "An ageing population can create challenges, but it can also bring experience and social contributions."
      ],
      quickCheck: [
        {
          question:
            "What does a population pyramid show?",
          options: [
            "Age and sex structure",
            "Only total population",
            "Only population density",
            "Only migration routes"
          ],
          answer: "Age and sex structure",
          explanation:
            "Population pyramids show age groups and sex composition."
        },
        {
          question:
            "What does a narrow top usually suggest?",
          options: [
            "Few older people",
            "High life expectancy",
            "No children",
            "High immigration only"
          ],
          answer: "Few older people",
          explanation:
            "A narrow top means fewer people survive into older age groups."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how a population pyramid can help governments plan for the future.",
          marks: 6,
          indicativeContent:
            "A population pyramid shows the number of people in different age groups. If there are many children, governments may need more schools, teachers and future jobs. If there are many older people, they may need more healthcare, pensions and care services. This helps governments plan spending and infrastructure."
        }
      ],
      grade9Move:
        "When interpreting a population pyramid, describe first, then explain. For example: ‘The base is wide, suggesting a high birth rate, which may increase demand for schools.’"
    },

    {
      id: "geo6-5-migration",
      title: "Migration",
      slug: "migration",
      pageRef: "Source booklet p.80",
      learningObjectives: [
        "Define migration.",
        "Explain push and pull factors.",
        "Assess the impacts of migration on origin and destination areas."
      ],
      keywords: [
        {
          term: "Migration",
          definition:
            "The movement of people from one place to another to live, either temporarily or permanently."
        },
        {
          term: "Immigration",
          definition:
            "Movement of people into a place."
        },
        {
          term: "Emigration",
          definition:
            "Movement of people out of a place."
        },
        {
          term: "Push factor",
          definition:
            "A negative factor that encourages people to leave a place."
        },
        {
          term: "Pull factor",
          definition:
            "A positive factor that attracts people to a place."
        },
        {
          term: "Refugee",
          definition:
            "A person forced to leave their country because of war, persecution or danger."
        },
        {
          term: "Economic migrant",
          definition:
            "A person who moves mainly to improve income or job opportunities."
        }
      ],
      tutorial: [
        {
          heading: "Types of migration",
          body:
            "Migration can be internal, within a country, or international, between countries. It can be voluntary, such as moving for work, or forced, such as fleeing conflict or natural disaster."
        },
        {
          heading: "Push and pull factors",
          body:
            "Push factors include war, unemployment, poverty, natural disasters, poor services and lack of opportunities. Pull factors include jobs, safety, better healthcare, education, higher wages and family links."
        },
        {
          heading: "Impacts of migration",
          body:
            "Migration affects both the origin and destination. Origin areas may lose workers, but receive money sent home. Destination areas may gain workers and cultural diversity, but may face pressure on housing, schools and healthcare."
        }
      ],
      workedExamples: [
        {
          question:
            "Give two push factors and two pull factors.",
          answer:
            "Push factors include unemployment and conflict. Pull factors include better-paid jobs and safer living conditions."
        },
        {
          question:
            "Explain one benefit of migration for a destination area.",
          answer:
            "Migration can provide workers for industries such as healthcare, construction, transport and hospitality, helping the economy grow."
        }
      ],
      commonMisconceptions: [
        "Immigration and emigration describe direction. Immigrants enter; emigrants leave.",
        "Not all migrants are refugees. Refugees are forced to leave because of danger or persecution.",
        "Migration can have both positive and negative effects for the same place."
      ],
      quickCheck: [
        {
          question:
            "Which is a pull factor?",
          options: [
            "War",
            "Drought",
            "Better job opportunities",
            "Poor healthcare"
          ],
          answer: "Better job opportunities",
          explanation:
            "Pull factors attract people to a place."
        },
        {
          question:
            "What is emigration?",
          options: [
            "Moving out of a place",
            "Moving into a place",
            "The number of births",
            "The average population density"
          ],
          answer: "Moving out of a place",
          explanation:
            "Emigration means leaving a place to live elsewhere."
        }
      ],
      examPractice: [
        {
          question:
            "Explain the impacts of migration on both origin and destination areas.",
          marks: 8,
          indicativeContent:
            "Origin areas may lose young workers, causing labour shortages and an ageing population. However, migrants may send money home as remittances. Destination areas may gain workers, skills and cultural diversity, supporting economic growth. However, rapid migration can increase pressure on housing, schools, healthcare and infrastructure. A balanced answer should consider both positive and negative impacts."
        }
      ],
      grade9Move:
        "For migration questions, structure your answer by place: origin positives, origin negatives, destination positives, destination negatives."
    },

    {
      id: "geo6-6-urbanisation",
      title: "Urbanisation",
      slug: "urbanisation",
      pageRef: "Source booklet p.84",
      learningObjectives: [
        "Define urbanisation.",
        "Explain why urbanisation happens.",
        "Compare urbanisation in richer and poorer countries."
      ],
      keywords: [
        {
          term: "Urbanisation",
          definition:
            "An increase in the proportion of people living in towns and cities."
        },
        {
          term: "Rural-urban migration",
          definition:
            "Movement of people from countryside areas to towns and cities."
        },
        {
          term: "Natural increase",
          definition:
            "Population growth when births are higher than deaths."
        },
        {
          term: "Urban growth",
          definition:
            "An increase in the size or population of an urban area."
        },
        {
          term: "Megacity",
          definition:
            "A city with a population of more than 10 million people."
        },
        {
          term: "Counter-urbanisation",
          definition:
            "Movement of people from urban areas to smaller towns or rural areas."
        }
      ],
      tutorial: [
        {
          heading: "What urbanisation means",
          body:
            "Urbanisation is not just cities getting bigger. It means a higher proportion of a country’s population living in urban areas. A country can urbanise because city populations grow faster than rural populations."
        },
        {
          heading: "Why people move to cities",
          body:
            "People may move from rural areas to cities because cities offer jobs, schools, healthcare, entertainment, transport and perceived better opportunities. Rural push factors may include poor farming income, lack of services and mechanisation reducing farm jobs."
        },
        {
          heading: "Urbanisation and development",
          body:
            "Many richer countries urbanised earlier during industrialisation. Many poorer or newly emerging countries are urbanising rapidly today due to rural-urban migration and natural increase in cities."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain how rural-urban migration contributes to urbanisation.",
          answer:
            "People move from countryside areas to towns and cities for jobs, education and services. This increases the number and proportion of people living in urban areas."
        },
        {
          question:
            "Why can urbanisation be rapid in poorer countries?",
          answer:
            "Urbanisation can be rapid where rural poverty pushes people out of the countryside, while cities attract people with jobs and services. High birth rates in urban areas can also increase city populations."
        }
      ],
      commonMisconceptions: [
        "Urbanisation is about the proportion of people living in urban areas, not just the total number.",
        "Urban growth and urbanisation are linked but not identical.",
        "Cities can grow because of natural increase as well as migration."
      ],
      quickCheck: [
        {
          question:
            "What is urbanisation?",
          options: [
            "An increase in the proportion of people living in towns and cities",
            "A decrease in city size",
            "Movement from cities to villages only",
            "The total number of farms in a country"
          ],
          answer:
            "An increase in the proportion of people living in towns and cities",
          explanation:
            "Urbanisation is measured as a proportion of population living in urban areas."
        },
        {
          question:
            "Which is a common pull factor attracting people to cities?",
          options: [
            "Better job opportunities",
            "Poor services",
            "Low wages",
            "Crop failure"
          ],
          answer: "Better job opportunities",
          explanation:
            "Jobs are a major pull factor for rural-urban migration."
        }
      ],
      examPractice: [
        {
          question:
            "Explain why urbanisation is happening rapidly in many poorer countries.",
          marks: 6,
          indicativeContent:
            "Urbanisation is rapid because rural areas may have poverty, limited services and fewer jobs, pushing people to leave. Cities attract people with the possibility of work, education, healthcare and better services. Natural increase in cities can also be high, increasing the urban population further."
        }
      ],
      grade9Move:
        "Distinguish between rural push factors, urban pull factors and natural increase. This creates a much stronger explanation."
    },

    {
      id: "geo6-7-urban-issues",
      title: "Urban Issues",
      slug: "urban-issues",
      pageRef: "Source booklet p.85",
      learningObjectives: [
        "Identify social, economic and environmental issues in cities.",
        "Explain how rapid urban growth creates pressure on services.",
        "Evaluate opportunities and challenges in urban areas."
      ],
      keywords: [
        {
          term: "Infrastructure",
          definition:
            "Basic systems and services such as roads, water supply, sewers, electricity and public transport."
        },
        {
          term: "Sanitation",
          definition:
            "Systems for safely removing sewage and waste."
        },
        {
          term: "Congestion",
          definition:
            "Overcrowding on roads or transport networks."
        },
        {
          term: "Pollution",
          definition:
            "The contamination of air, water or land by harmful substances."
        },
        {
          term: "Inequality",
          definition:
            "Uneven access to wealth, services and opportunities."
        },
        {
          term: "Housing shortage",
          definition:
            "A lack of enough affordable and suitable homes."
        }
      ],
      tutorial: [
        {
          heading: "Urban opportunities",
          body:
            "Cities can offer employment, education, healthcare, transport, entertainment and access to markets. They can be centres of innovation, trade and culture."
        },
        {
          heading: "Urban challenges",
          body:
            "Rapid urban growth can create pressure on housing, clean water, sanitation, roads, schools and hospitals. If city growth is faster than planning and investment, services may not keep up."
        },
        {
          heading: "Inequality within cities",
          body:
            "Cities often contain wealthy areas and poorer informal areas close together. This means average city data can hide sharp inequalities in income, housing quality and access to services."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain how rapid urban growth can lead to congestion.",
          answer:
            "As more people move to a city, more people need to travel for work, school and services. If roads and public transport do not expand quickly enough, traffic increases and congestion becomes worse."
        },
        {
          question:
            "Explain one economic opportunity created by cities.",
          answer:
            "Cities provide a large workforce and large markets, attracting businesses. This creates jobs and can increase income for residents."
        }
      ],
      commonMisconceptions: [
        "Urban areas create opportunities as well as problems.",
        "Not everyone in a city experiences the same quality of life.",
        "Urban issues are not only environmental; they can be social and economic too."
      ],
      quickCheck: [
        {
          question:
            "Which is an example of urban infrastructure?",
          options: [
            "Roads and water pipes",
            "Birth rate",
            "Latitude",
            "A mountain range"
          ],
          answer: "Roads and water pipes",
          explanation:
            "Infrastructure means the basic systems and services needed by a settlement."
        },
        {
          question:
            "Which problem is linked to rapid urban growth?",
          options: [
            "Pressure on housing",
            "Lower demand for services",
            "No need for transport",
            "A guaranteed fall in pollution"
          ],
          answer: "Pressure on housing",
          explanation:
            "Fast population growth can create housing shortages."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how rapid urban growth can create social and environmental challenges.",
          marks: 6,
          indicativeContent:
            "Rapid urban growth can create social challenges because housing, schools and hospitals may not keep up with demand. This can lead to overcrowding and poor access to services. Environmental challenges include air pollution from traffic, water pollution from poor sanitation and increased waste if collection systems are inadequate."
        }
      ],
      grade9Move:
        "Organise urban issues using social, economic and environmental categories. This helps you produce balanced, exam-ready explanations."
    },

    {
      id: "geo6-8-squatter-settlements",
      title: "Squatter Settlements",
      slug: "squatter-settlements",
      pageRef: "Source booklet p.87",
      learningObjectives: [
        "Define squatter settlement.",
        "Explain why squatter settlements develop.",
        "Describe challenges and improvements in informal settlements."
      ],
      keywords: [
        {
          term: "Squatter settlement",
          definition:
            "An informal settlement where people build homes on land they do not legally own."
        },
        {
          term: "Informal settlement",
          definition:
            "Housing built without formal planning permission, often lacking basic services."
        },
        {
          term: "Self-help scheme",
          definition:
            "A strategy where residents are supported to improve their own homes and services."
        },
        {
          term: "Site and service scheme",
          definition:
            "A scheme where authorities provide land and basic services so residents can build homes."
        },
        {
          term: "Tenure",
          definition:
            "The legal right to occupy or own land or property."
        },
        {
          term: "Basic services",
          definition:
            "Essential services such as clean water, toilets, drainage, electricity and waste collection."
        }
      ],
      tutorial: [
        {
          heading: "Why squatter settlements develop",
          body:
            "Squatter settlements often develop when urban growth is faster than the supply of affordable housing. Migrants may arrive with little money and build homes on unused or marginal land near job opportunities."
        },
        {
          heading: "Challenges",
          body:
            "Informal settlements may lack clean water, sanitation, electricity, waste collection and secure housing. Homes may be built from weak materials and located on risky land such as steep slopes, floodplains or polluted areas."
        },
        {
          heading: "Improving informal settlements",
          body:
            "Improvements can include self-help schemes, legal land tenure, water pipes, toilets, drainage, paved roads, electricity and schools. Upgrading can improve quality of life without forcing communities to move."
        }
      ],
      workedExamples: [
        {
          question:
            "Why do people live in squatter settlements?",
          answer:
            "People may not be able to afford formal housing, but they still need to live near jobs and services in the city. Informal settlements provide a low-cost way to access urban opportunities."
        },
        {
          question:
            "Explain one advantage of a self-help scheme.",
          answer:
            "A self-help scheme can be cheaper than demolishing and rebuilding because residents improve their own homes with support, materials or legal rights from authorities."
        }
      ],
      commonMisconceptions: [
        "Squatter settlements are not simply places of poverty; they can have strong communities and informal economies.",
        "Demolishing informal settlements does not automatically solve housing problems.",
        "Providing legal tenure can encourage residents to invest in improving their homes."
      ],
      quickCheck: [
        {
          question:
            "What is a squatter settlement?",
          options: [
            "An informal settlement built on land people do not legally own",
            "A planned wealthy suburb",
            "A rural farming village",
            "A coastal erosion defence"
          ],
          answer:
            "An informal settlement built on land people do not legally own",
          explanation:
            "Squatter settlements are informal and usually lack formal land ownership."
        },
        {
          question:
            "Which service is often lacking in informal settlements?",
          options: [
            "Clean water and sanitation",
            "Luxury shopping centres",
            "Airports",
            "High-speed rail stations only"
          ],
          answer: "Clean water and sanitation",
          explanation:
            "Basic services may be limited or absent in informal settlements."
        }
      ],
      examPractice: [
        {
          question:
            "Explain why squatter settlements develop in rapidly growing cities.",
          marks: 6,
          indicativeContent:
            "Squatter settlements develop when cities grow faster than affordable housing supply. Rural-urban migrants may arrive with little money but need to live near jobs and services. If formal housing is too expensive, people build informal homes on unused or marginal land, often without legal ownership or basic services."
        },
        {
          question:
            "Evaluate strategies used to improve squatter settlements.",
          marks: 8,
          indicativeContent:
            "Self-help schemes can improve housing while keeping communities together and reducing cost. Site and service schemes provide land and basic infrastructure, allowing residents to build gradually. Legal tenure encourages people to invest in homes. However, improvements can be slow, funding may be limited, and some risky locations may still be unsuitable. A balanced judgement should consider cost, speed, safety and community impact."
        }
      ],
      grade9Move:
        "Avoid describing informal settlements only negatively. High-level answers recognise community, informal employment and practical upgrading strategies."
    },

    {
      id: "geo6-9-urbanisation-environmental-issues",
      title: "Urbanisation Environmental Issues",
      slug: "urbanisation-environmental-issues",
      pageRef: "Source booklet p.88",
      learningObjectives: [
        "Explain environmental problems caused by urban growth.",
        "Describe how cities can become more sustainable.",
        "Evaluate solutions to urban environmental issues."
      ],
      keywords: [
        {
          term: "Air pollution",
          definition:
            "Harmful gases and particles in the air, often from traffic, industry and energy use."
        },
        {
          term: "Water pollution",
          definition:
            "Contamination of rivers, lakes or groundwater by sewage, chemicals or waste."
        },
        {
          term: "Urban heat island",
          definition:
            "When urban areas are warmer than surrounding rural areas because of buildings, roads, energy use and reduced vegetation."
        },
        {
          term: "Waste management",
          definition:
            "The collection, treatment, recycling or disposal of waste."
        },
        {
          term: "Sustainable city",
          definition:
            "A city designed to meet people’s needs while reducing environmental damage and protecting resources for the future."
        },
        {
          term: "Green space",
          definition:
            "Parks, trees, gardens and natural areas within an urban environment."
        }
      ],
      tutorial: [
        {
          heading: "Air pollution",
          body:
            "Rapid urbanisation can increase traffic, industry and energy use. This can release pollutants into the air, causing breathing problems, reducing quality of life and contributing to climate change."
        },
        {
          heading: "Water and waste",
          body:
            "If sanitation and waste systems do not keep up with population growth, sewage and rubbish can pollute rivers and land. This can spread disease and damage ecosystems."
        },
        {
          heading: "Sustainable urban solutions",
          body:
            "Solutions include public transport, cycling routes, renewable energy, recycling, clean water systems, waste treatment, urban green spaces, tree planting and better planning."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain how urbanisation can cause water pollution.",
          answer:
            "Rapid population growth increases sewage and waste. If sanitation systems are poor or overloaded, untreated sewage and rubbish may enter rivers, polluting water and spreading disease."
        },
        {
          question:
            "Explain how green spaces improve urban environments.",
          answer:
            "Green spaces absorb carbon dioxide, provide shade, reduce the urban heat island effect, improve air quality and create recreational areas for residents."
        }
      ],
      commonMisconceptions: [
        "Urban environmental issues are not unavoidable; planning and technology can reduce them.",
        "Green space is not just decorative; it can reduce heat, improve air quality and manage surface runoff.",
        "Public transport can reduce congestion and air pollution if it is affordable, reliable and well-connected."
      ],
      quickCheck: [
        {
          question:
            "What is the urban heat island effect?",
          options: [
            "Cities being warmer than nearby rural areas",
            "Rural areas being warmer than cities",
            "A type of coastal erosion",
            "A form of population pyramid"
          ],
          answer: "Cities being warmer than nearby rural areas",
          explanation:
            "Concrete, tarmac, buildings, vehicles and reduced vegetation can make urban areas warmer."
        },
        {
          question:
            "Which strategy can reduce urban air pollution?",
          options: [
            "Improving public transport",
            "Increasing traffic congestion",
            "Removing all green spaces",
            "Dumping waste in rivers"
          ],
          answer: "Improving public transport",
          explanation:
            "Good public transport can reduce car use and emissions."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how rapid urbanisation can create environmental problems.",
          marks: 6,
          indicativeContent:
            "Rapid urbanisation can increase traffic and industry, causing air pollution and greenhouse gas emissions. If waste collection and sanitation do not keep up with population growth, rubbish and sewage may pollute rivers and land. Urban expansion can also remove vegetation, increasing surface runoff and contributing to the urban heat island effect."
        },
        {
          question:
            "Evaluate ways cities can become more sustainable.",
          marks: 8,
          indicativeContent:
            "Cities can become more sustainable by investing in public transport, cycling, renewable energy, recycling, green spaces, clean water and waste treatment. These reduce pollution and improve quality of life. However, they require funding, planning and maintenance. Success depends on whether solutions are affordable, accessible and used by residents."
        }
      ],
      grade9Move:
        "For environmental urbanisation answers, link cause → process → impact → solution. Example: more traffic → more emissions → poorer air quality → public transport and cycling routes."
    }
  ],

  sectionReview: {
    title: "Section Six Review — Population and Urbanisation",
    summary:
      "Population and urbanisation are central human geography themes. People are unevenly distributed because of physical and human factors. Population density helps compare places but can hide variation. Populations grow through natural increase and migration. Population structures affect planning for schools, jobs, healthcare and pensions. Migration and urbanisation create opportunities but also social, economic and environmental challenges.",
    mustKnow: [
      "Population distribution means the pattern of where people live.",
      "Population density = population ÷ area.",
      "High density means many people per unit area; low density means few people per unit area.",
      "Population growth happens through natural increase and immigration.",
      "Natural increase happens when birth rate is higher than death rate.",
      "Population pyramids show age and sex structure.",
      "A wide pyramid base usually suggests a youthful population and high birth rate.",
      "Migration is the movement of people from one place to another.",
      "Push factors make people leave; pull factors attract people.",
      "Urbanisation is an increase in the proportion of people living in towns and cities.",
      "Rapid urbanisation can cause housing shortages, congestion, pollution and pressure on services.",
      "Squatter settlements develop when affordable housing cannot keep up with urban growth.",
      "Sustainable urban planning can reduce pollution and improve quality of life."
    ],
    highValueExamPhrases: [
      "This creates an uneven pattern of population distribution...",
      "The area is densely populated because physical and human factors make settlement easier...",
      "The population density is an average, so it can hide local variation...",
      "Death rates fall faster than birth rates, creating rapid natural increase...",
      "The wide base of the population pyramid suggests a youthful population...",
      "Push factors encourage people to leave, while pull factors attract them to the destination...",
      "Rapid urban growth places pressure on housing, sanitation and infrastructure...",
      "Informal settlements develop when formal housing supply cannot keep up with demand...",
      "A sustainable solution must reduce environmental damage while improving quality of life..."
    ],
    mixedPractice: [
      {
        question:
          "Explain why population distribution is uneven across the world.",
        marks: 6,
        indicativeContent:
          "Population distribution is uneven because physical factors such as climate, relief, water supply and soil affect how easy it is to live in a place. Lowland areas with fertile soil and reliable water are often densely populated. Deserts, mountains and polar regions are often sparsely populated. Human factors such as jobs, transport, services and trade also attract people to some areas."
      },
      {
        question:
          "A country has a population of 45,000,000 and an area of 300,000 km². Calculate the population density.",
        marks: 2,
        indicativeContent:
          "45,000,000 ÷ 300,000 = 150 people per km²."
      },
      {
        question:
          "Explain why rapid population growth can create challenges for a country.",
        marks: 6,
        indicativeContent:
          "Rapid population growth can increase demand for schools, healthcare, housing, food and jobs. If services and infrastructure cannot keep up, overcrowding and inequality may increase. A youthful population may create future economic opportunities, but only if education and employment are available."
      },
      {
        question:
          "Explain the causes and effects of rural-urban migration.",
        marks: 8,
        indicativeContent:
          "Rural-urban migration is caused by push factors such as rural poverty, lack of services, poor farming income and limited jobs. Pull factors include employment, education, healthcare and better services in cities. Effects include urban growth, pressure on housing and services, and sometimes the development of informal settlements. Rural areas may lose young workers but may benefit from remittances."
      },
      {
        question:
          "Evaluate strategies used to manage environmental issues in rapidly growing cities.",
        marks: 8,
        indicativeContent:
          "Strategies include public transport, cycling routes, recycling, waste treatment, clean water systems, renewable energy and green spaces. These can reduce pollution, improve health and make cities more sustainable. However, they require funding, planning and public use. Some rapidly growing cities may struggle to provide these solutions quickly enough, so effectiveness depends on governance, cost and scale."
      }
    ]
  }
};

export default geographyContent6;